import React from 'react'
import { useNetworkScope } from '../../hooks/useNetworkScope'
import { PdpAuditReport, toCtaProductRef } from './PdpAuditReport'
import { PdpAudit } from './types'
import { rowToResponse } from './reportFiles'
import { MultiAuditSynthesisReport } from './MultiAuditSynthesisReport'
import {
  ReportProductRef,
  RmnAuditReportType,
  RmnBrandReportParams,
  RmnCategoryReportParams,
  RmnComparisonReportParams,
  RmnMultiAuditReport,
} from '../../types/rmn'

// Shared pieces of the audit-report surfaces (TDC-1499 phase 3): the report
// history sections now live on the Catalog pages (categories/brands), and
// the deep-link viewer survives in its own page — everything they share
// (row shape, labels, the render dispatch) lives here.

export const PAGE_SIZE = 10
export const POLL_INTERVAL_MS = 8000

// Row shape from GET /rmn/audit/reports (rmnControl-scoped).
export interface RmnAuditReportRow {
  id: number
  url: string
  report_type: string
  status: string
  created_at: string
  params?: RmnCategoryReportParams | RmnComparisonReportParams | Record<string, any> | null
  generation_error?: string | null
  user?: { userid: number; firstName?: string; lastName?: string } | null
  report: PdpAudit | RmnMultiAuditReport<PdpAudit> | null
  // Keyed by audited URL — the report's own URL for pdp rows, the per-item
  // URLs for category/brand/comparison rows. Optional so the portal renders
  // against an API that predates it.
  product_refs?: Record<string, ReportProductRef>
}

export const rowLabel = (row: RmnAuditReportRow): string => {
  if (row.report_type === 'category') {
    return (row.params as RmnCategoryReportParams | null | undefined)?.category_name || row.url
  }
  if (row.report_type === 'brand') {
    return (row.params as RmnBrandReportParams | null | undefined)?.brand_name || row.url
  }
  if (row.report_type === 'comparison') {
    const urls = (row.params as RmnComparisonReportParams | null | undefined)?.urls || []
    return urls.length ? `${urls.length} pages · ${urls[0]}` : row.url
  }
  return row.url
}

// The report's Create Strategy CTA opens the portal's wizard, not the public
// /proposal page — prefixed inside a brand-portal workspace, slug-free on
// the brand routers (TDC-1499).
export function useRmnProposalPath(): string | undefined {
  const { basePath } = useNetworkScope()
  return `${basePath}/opportunities/strategies/new`
}

// A history row's Create strategy: the wizard reads ?audit=<id> and asks
// the server to suggest the category and goals from the whole report. A
// single-page row also carries what the report's own CTA sends (page,
// product name, catalog product ref).
export function strategyQueryForRow(row: RmnAuditReportRow): string {
  const params = new URLSearchParams({ audit: String(row.id) })
  if (row.report_type === 'pdp') {
    const audit = row.report as PdpAudit | null
    if (row.url) params.set('url', row.url)
    if (audit && audit.productName) params.set('title', audit.productName)
    const ref = (row.product_refs || {})[row.url]
    if (ref && ref.desireid) params.set('desireid', String(ref.desireid))
    else if (ref && ref.discovered_product_id) params.set('dp', String(ref.discovered_product_id))
    if (ref && ref.product_category_id) params.set('category', String(ref.product_category_id))
  }
  return params.toString()
}

// Renders the right report body for an opened row: single-PDP audits use the
// classic renderer; category/brand/comparison rows use the synthesis renderer.
export function ReportDialogBody({ row }: { row: RmnAuditReportRow }) {
  const proposalPath = useRmnProposalPath()
  if (!row.report) return null
  const refs = row.product_refs || {}
  if (row.report_type === 'pdp') {
    return (
      <PdpAuditReport
        data={rowToResponse(row)}
        isLoggedIn
        proposalPath={proposalPath}
        proposalCtaLabel="Create Strategy"
        showEmailAction={false}
        productRef={toCtaProductRef(refs[row.url])}
      />
    )
  }
  return (
    <MultiAuditSynthesisReport
      reportType={row.report_type}
      reportId={row.id}
      report={row.report as RmnMultiAuditReport<PdpAudit>}
      params={
        row.params as RmnCategoryReportParams | RmnBrandReportParams | RmnComparisonReportParams | null
      }
      proposalPath={proposalPath}
      proposalCtaLabel="Create Strategy"
      productRefs={refs}
    />
  )
}

export const SECTIONS: Record<RmnAuditReportType, { title: string; newLabel: string; empty: string }> = {
  pdp: {
    title: 'Single PDP Audits',
    newLabel: 'New PDP Audit',
    empty: 'No audits yet — run your first product page audit to see it here.',
  },
  category: {
    title: 'Category Reports',
    newLabel: 'New Category Report',
    empty: "No category reports yet — run one from a category row above.",
  },
  brand: {
    title: 'Brand Reports',
    newLabel: 'New Brand Report',
    empty: "No brand reports yet — run one from a brand row above.",
  },
  comparison: {
    title: 'PDP Comparisons',
    newLabel: 'New Comparison',
    empty: 'No comparisons yet — rank 2–5 of your product pages side by side.',
  },
}

export const LIST_LABEL_HEADERS: Record<RmnAuditReportType, string> = {
  pdp: 'URL',
  category: 'Category',
  brand: 'Brand',
  comparison: 'Pages',
}

// Caption for rows that can't be opened yet (generating / failed / legacy
// rows without a stored report).
export const unopenableCaption = (row: RmnAuditReportRow): string => {
  if (row.status === 'generating') return 'Generating…'
  if (row.status === 'error') return row.generation_error || 'Failed'
  return 'Unavailable'
}
